import colors from '@honeybook/hbui/dist/styles/colors';
import spacing from '@honeybook/hbui/dist/styles/spacing';
import { truncatedText } from '@honeybook/hbui/dist/styles/mixins';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import styled, { css } from 'styled-components';
import is from 'styled-is';
import { opacityHex } from '@honeybook/hbui/dist/utils/color.utils';
import {
    FlowGalleryCardActionsContainer,
    FlowGalleryCardActionsListItem
} from '~/components/common/FlowTemplates/FlowGalleryCard/FlowGalleryCardActions';
import {
    TFlowGalleryCardSize,
    TFlowGalleryCardTheme,
    TFlowGalleryCardVariant
} from '~/components/FlowGallery/types/flow-gallery.types';

const getVariantStyles = (variant?: TFlowGalleryCardVariant) => {
    if (variant === 'minimal') {
        return css`
            ${FlowGalleryCardActionsContainer} {
                margin-top: 0;
            }

            ${FlowGalleryCardPoster} {
                margin-bottom: 0;
            }

            ${FlowGalleryCardPosterContainer} {
                margin-bottom: 12px;
            }
        `;
    }
};

const getThemeStyles = (theme?: TFlowGalleryCardTheme) => {
    if (theme === 'dark') {
        return css`
            ${FlowGalleryCardTitle}, ${FlowGalleryCardDescription}, ${FlowGalleryCardActionsListItem} {
                color: ${colors.white};
            }

            ${FlowGalleryCardPosterFrame} {
                background-color: ${colors.white_03};
            }

            ${FlowGalleryCardPosterFrameDot} {
                background-color: ${colors.white_02};
            }
        `;
    }
};

export const StyledFlowGalleryCardActions = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: ${opacityHex(colors.white, 40)};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease;
`;

export const DEFAULT_FLOW_GALLERY_CARD_SIZE = {
    sm: { height: 240, width: 453 },
    md: { height: 302, width: 438 }
} as const;

export const TEMPLATE_GALLERY_CARD_STYLES = {
    size: DEFAULT_FLOW_GALLERY_CARD_SIZE
} as const;

export const POSTER_BOX_SHADOW_VALUE = '0 10px 20px 0 rgb(0 18 71 / 15%)';

export const FlowGalleryCardPosterContainer = styled.div<{
    isPointer: boolean;
    isSmallSpacing?: boolean;
}>`
    display: flex;
    flex-direction: column;
    box-shadow: 0 1px 3px rgba(0, 18, 71, 0.16);
    border-radius: 3px;
    margin-bottom: ${props =>
        props.isSmallSpacing ? spacing[16] : spacing[24]};
    ${is('isPointer')`
        cursor: pointer;
    `}
`;

export const FlowGalleryCardPoster = styled.div<{
    image: string;
    size: TFlowGalleryCardSize['sm'];
}>`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: ${props => props.size.height}px;
    background-image: url(${({ image }) => image});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 0 0 3px 3px;

    transition: all 0.2s ease;
    &:hover {
        box-shadow: ${POSTER_BOX_SHADOW_VALUE};

        ${StyledFlowGalleryCardActions} {
            visibility: visible;
            opacity: 1;
        }
    }
`;

export const FlowGalleryCardPosterFrame = styled.div`
    width: 100%;
    height: 10px;
    display: flex;
    align-items: center;
    background-color: ${colors.neutral200};
    border-radius: 3px 3px 0 0;
`;

export const FlowGalleryCardPosterFrameDot = styled.span`
    width: 3.5px;
    height: 3.5px;
    display: block;
    border-radius: 100%;
    margin-left: ${spacing[4]};
    background-color: ${colors.neutral400};
`;

export const FlowGalleryCardTitle = styled(HBText)<{
    isLargeSpacing?: boolean;
}>`
    margin-bottom: ${props => (props.isLargeSpacing ? spacing[8] : spacing[4])};
    ${truncatedText};
    display: block;
`;

export const FlowGalleryCardDescription = styled(HBText)<{
    showLongDescription?: boolean;
}>`
    display: -webkit-box;
    -webkit-line-clamp: ${props => (props.showLongDescription ? 'unset' : 2)};
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

export const FlowGalleryCardContent = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

export const FlowGalleryCardContainer = styled.div<{
    variant?: TFlowGalleryCardVariant;
    theme?: TFlowGalleryCardTheme;
}>`
    background-color: transparent;
    display: flex;
    flex-direction: column;

    ${props => getVariantStyles(props.variant)}
    ${props => getThemeStyles(props.theme)}
`;
